import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Loading } from '@brighterion/br-ui';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import useImportListEntries from 'Hooks/query-hooks/listEntry/useImportListEntries';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
// eslint-disable-next-line max-len
import useGetUpdatedSingleListAndStartImportOrExport from 'Routes/ListManagement/routes/hooks/useGetUpdatedSingleListAndStartImportOrExport';
import { IMPORT_EXPORT_PROCESS_TYPE } from 'Common/enums';

import styles from './Importer.css';

type ImporterActionButtonsProps = {
    listName: string;
    listId: number;
    importDisabled: boolean;
    handleFileDelete: () => void;
    importFileName?: string;
};

/** Renders Cancel & Start Import Button for the Import Entries from CSV View */
const ImporterActionButtons = ({
    listName,
    listId,
    handleFileDelete,
    importDisabled,
    importFileName = ''
}: ImporterActionButtonsProps) => {
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation();
    const { addModal, pop } = useReduxModal();
    const importListEntriesMutation = useImportListEntries({});
    const startImportEntries = useGetUpdatedSingleListAndStartImportOrExport({
        currentListId: listId,
        processType: IMPORT_EXPORT_PROCESS_TYPE.IMPORT,
        mutation: importListEntriesMutation
    });

    // copy pasta from previous FileUploader/Bulk-Upload code. Determines list page route.
    const routeToGoTo = location.pathname.split('/').slice(0, 3).join('/');

    const handleStartImport = () => {
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Lists - Import List Entries',
                text: 'Lists - Import List Entries'
            }
        });
        startImportEntries({
            listId,
            listName,
            fileName: importFileName
        });
    };

    const handleConfirmCancellation = (): void => {
        handleFileDelete();
        pop('confirm-import-cancel');
        history.push(routeToGoTo);
    };

    const handleCancel = (): void => {
        const action = t('Import entries from CSV');
        const modalBody = (
            <Trans i18nKey='Confirm Cancel Import List'>
                {{ action }} {{ listName }}
            </Trans>
        );
        addModal({
            id: 'confirm-import-cancel',
            status: 'warning',
            title: t('Cancel Import'),
            content: <div>{modalBody}</div>,
            showCloseIcon: true,
            showCloseButton: true,
            onAction: handleConfirmCancellation,
            onActionText: t('Confirm Cancellation'),
            closeOnAction: false
        });
    };

    return (
        <div className={styles.buttonsRow} data-testid='list-entries-import-actions'>
            <Button
                variant='tertiary'
                className={styles.cancelButton}
                size='small'
                data-testid='cancel-import-button'
                onClick={handleOnClickEvent(handleCancel)}
                onKeyDown={handleKeyEvent(handleCancel)}
                aria-label='cancel import entries'
                tabIndex={0}
                disabled={importListEntriesMutation.isLoading}
            >
                {t('Cancel')}
            </Button>
            <Button
                variant='primary'
                size='small'
                data-testid='start-import-button'
                onClick={handleOnClickEvent(handleStartImport)}
                onKeyDown={handleKeyEvent(handleStartImport)}
                aria-label='start import'
                tabIndex={0}
                disabled={importListEntriesMutation.isLoading || importDisabled}
            >
                {importListEntriesMutation.isLoading ? <Loading size='sm' fillName='white' /> : t('Start Import')}
            </Button>
        </div>
    );
};

export default ImporterActionButtons;
